import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Typography,
  Modal,
  Form,
  Input,
  Message,
} from '@arco-design/web-react';
import {
  IconHome,
  IconHistory,
  IconUser,
  IconLock,
} from '@arco-design/web-react/icon';
import { useAppDispatch, useAppSelector } from '../../store';
import { login, logout, clearError } from '../../store/slices/authSlice';

const { Header, Sider, Content, Footer } = Layout;
const MenuItem = Menu.Item;
const FormItem = Form.Item;

const MainLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, error } = useAppSelector((state) => state.auth);
  const [collapsed, setCollapsed] = useState(false);
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const [form] = Form.useForm();
  const siderWidth = collapsed ? 48 : 220; // 侧边栏展开宽度220px，收缩后宽度48px

  // 处理退出
  const handleLogout = useCallback(async () => {
    try {
      dispatch(logout());
      navigate('/');
    } catch (error) {
      console.error('退出失败:', error);
    }
  }, [dispatch, navigate]);

  // 快捷键监听
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 检查是否按下 Ctrl + Alt + L
      if (e.ctrlKey && e.altKey && e.key.toLowerCase() === 'l') {
        e.preventDefault();
        if (!user) {
          setLoginModalVisible(true);
        }
      }
      // 检查是否按下 Ctrl + Alt + E
      if (e.ctrlKey && e.altKey && e.key.toLowerCase() === 'e') {
        e.preventDefault();
        if (user) {
          handleLogout();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [user, handleLogout]);

  // 错误处理
  useEffect(() => {
    if (error) {
      Message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // 处理登录
  const handleLogin = async () => {
    try {
      const values = await form.validate();
      await dispatch(login(values)).unwrap();
      setLoginModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('登录失败:', error);
    }
  };

  // 处理侧边栏收缩状态变化
  const handleCollapse = (collapsed: boolean) => {
    setCollapsed(collapsed);
  };

  // 生成下拉菜单
  const dropList = (
    <div style={{ padding: '8px 0' }}>
      <div style={{ padding: '4px 12px', cursor: 'pointer' }} onClick={handleLogout}>
        退出登录
      </div>
    </div>
  );

  return (
    <Layout style={{ height: '100vh' }}>
      <Header style={{ 
        padding: '0 20px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        backgroundColor: '#1D2129', // 暗色背景
        color: '#fff',
        height: '60px', // 设置Header高度为60px
        position: 'fixed', // 固定位置
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <img src="/logo.png" alt="Logo" style={{ height: 32, marginRight: 12 }} />
          <Typography.Title heading={5} style={{ margin: 0, color: '#fff' }}>
            软件更新日志
          </Typography.Title>
        </Link>
        
        <div>
          {user ? (
            <Dropdown droplist={dropList} position="br">
              <Avatar style={{ backgroundColor: '#3370ff', cursor: 'pointer' }}>
                {user.username.charAt(0).toUpperCase()}
              </Avatar>
            </Dropdown>
          ) : null}
        </div>
      </Header>
      
      <Layout style={{ marginTop: '60px', height: 'calc(100vh - 60px)' }}>
        <Sider 
          collapsible 
          breakpoint="md" 
          width={220}
          collapsed={collapsed}
          onCollapse={handleCollapse}
          style={{ 
            height: 'calc(100vh - 60px)',
            position: 'fixed',
            left: 0,
            top: '60px',
            bottom: 0,
            zIndex: 900
          }}
        >
          <Menu
            selectedKeys={[location.pathname]}
            style={{ height: '100%' }}
          >
            <MenuItem key="/" onClick={() => navigate('/')}>
              <IconHome />
              首页
            </MenuItem>
            {user && (
              <MenuItem key="/update-history" onClick={() => navigate('/update-history')}>
                <IconHistory />
                历史管理
              </MenuItem>
            )}
            {user && user.role === 'ADMIN' && (
              <MenuItem key="/users" onClick={() => navigate('/users')}>
                <IconUser />
                用户管理
              </MenuItem>
            )}
          </Menu>
        </Sider>
        
        <Layout style={{ 
          marginLeft: `${siderWidth}px`,
          transition: 'margin-left 0.2s'
        }}>
          <Content style={{ 
            padding: '20px', 
            overflow: 'auto', 
            height: 'calc(100vh - 60px - 40px)'
          }}>
            <Outlet />
          </Content>
          
          <Footer style={{ textAlign: 'center', padding: '10px 20px' }}>
            软件更新日志 ©{new Date().getFullYear()}
          </Footer>
        </Layout>
      </Layout>

      {/* 登录对话框 */}
      <Modal
        title="登录"
        visible={loginModalVisible}
        onOk={handleLogin}
        onCancel={() => {
          setLoginModalVisible(false);
          form.resetFields();
        }}
        autoFocus={false}
        focusLock={true}
        maskClosable={false}
      >
        <Form 
          form={form}
          layout="vertical"
          autoComplete="off"
        >
          <FormItem
            label="用户名"
            field="username"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input 
              placeholder="请输入用户名" 
              prefix={<IconUser />} 
              autoComplete="username"
            />
          </FormItem>
          <FormItem
            label="密码"
            field="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password 
              placeholder="请输入密码" 
              prefix={<IconLock />} 
              autoComplete="current-password"
            />
          </FormItem>
        </Form>
      </Modal>
    </Layout>
  );
};

export default MainLayout; 